import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight } from 'lucide-react';

export default function Fundament() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      number: '01',
      title: 'Analyse av tomten',
      description: 'Det første og viktigste steget er en grundig vurdering av grunnforholdene og terrengets form. Dette er grunnlaget for valg av riktig fundamentløsning.',
      details: [
        'type grunn (fjell, sand, leire, fyllings grunn)',
        'nivå på grunnvann',
        'terreng helning',
        'tilgang til montasjestedet'
      ],
      conclusion: 'En riktig analyse forhindrer problemer med setninger, fuktighet og frostskader på fundamentet.'
    },
    {
      number: '02',
      title: 'Valg av fundamentsystem',
      description: 'Avhengig av tomtens forhold og kundens ønsker, brukes tre hovedløsninger.',
      subsections: [
        {
          title: 'Punktfundament',
          desc: 'En økonomisk løsning med punktformede betongstøtter eller prefabrikkerte fundamentblokker. Konstruksjonen hviler på bærende bjelker.'
        },
        {
          title: 'Platafundament',
          desc: 'En fullstendig armert betongplate lagt på isolasjons- og forsterkt lag. Sikrer høyest stabilitet og muliggjør integrering av installasjoner.'
        },
        {
          title: 'Søylefundament (søylefundament)',
          desc: 'Konstruksjon basert på stål- eller betongstøtter som minimerer kontakt mellom bygning og grunn. Spesielt anbefalt for naturlige og ujevne områder.'
        }
      ]
    },
    {
      number: '03',
      title: 'Forberedelse av terrenget',
      description: 'Fundamentet krever omhyggelig forberedelse av grunn og overflate for å sikre stabilitet i mange år.',
      subsections: [
        {
          title: '3.1 Rensing av overflaten',
          desc: 'Fjerning av vegetasjon, røtter og steiner, og avgraving av humussjikt (ca. 10–30 cm). Målet er å oppnå stabilt, bærende underlag.'
        },
        {
          title: '3.2 Stabilisering av grunn',
          desc: 'For grunn med utilstrekkelig bæreevne skal det bygges et lag av pukk (f.eks. grus 8–16 mm) som deretter komprimeres mekanisk. Dette sikrer jevn belastningsfordeling og forhindrer setninger.'
        },
        {
          title: '3.3 Nivellering',
          desc: 'Overflaten skal være nivellert (for platafundament) eller forberedt punktvis (for punkt- og søylefundamenter). Tillatte avvik skal være minimale (noen få millimeter).'
        },
        {
          title: '3.4 Dreneringssystem',
          desc: 'Sikre fall av terrenget bort fra bygningen, ingen bassenger med vann og eventuell drenering. Dette er kritisk i norske forhold hvor vann og frost kan skade fundamentet.'
        },
        {
          title: '3.5 Forberedelse av installasjoner',
          desc: 'I forberedelsesstadie må det planlegges for strømtilførsel, vann- og kloakktilkoblinger (hvis nødvendig) og installasjonsgjennomføringer.'
        }
      ]
    },
    {
      number: '04',
      title: 'Utførelse av fundamentet',
      description: 'Korrekt utførelse av fundamentet er avgjørende for hele konstruksjonens varighet.',
      subsections: [
        {
          title: 'Punktfundament',
          desc: 'Utførelse av punktedgruber under frostdybden, utstøping av betong eller montering av prefabrikkater, og presisjonsnivellering av alle punkt.'
        },
        {
          title: 'Platafundament',
          desc: 'Utgraving av hele området, legning av bærelaget med pukk, påføring av termisk isolasjon (EPS/XPS), armering og utstøping av betongplate.'
        },
        {
          title: 'Søylefundament',
          desc: 'Utførelse av punktfundamenter (betong eller jordskruer), montering av prefabrikkerte søyler, regulering av høyde og nivå. Denne løsningen krever høy presisjon.'
        }
      ]
    },
    {
      number: '05',
      title: 'Montering av ferdig enhet',
      description: 'Etter at fundamentet er klart, transporteres den ferdig monterte saunaenheten til stedet. Den plasseres forsiktig på fundamentet og forankres. Deretter tilkobles alle installasjoner — elektrisitet, vann (hvis aktuelt) og ventilasjon — og det gjennomføres endelige justeringer og tester.'
    },
    {
      number: '06',
      title: 'Tilkobling av installasjoner og avslutning',
      description: 'Siste fase omfatter tilkobling av elektrisk og vanninstallasjon, utførelse av ytre elementer (f.eks. terrasser, stier) og kontroll av nivå og stabilitet.'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative h-[600px] mb-0 overflow-hidden"
      >
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3bc4bf9ea_Zrzutekranu2026-04-11o162043.png"
          alt="Fundament"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg tracking-widest-2xl font-body uppercase text-white/60 mb-6">Prosess</p>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              Fundament
            </h1>
            <p className="font-body text-white/80 text-2xl max-w-2xl leading-relaxed">
              Grunnlaget for en solid badstue — prosess fra analyse til ferdigstillelse
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* INTRO */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20"
        >
          <div className="max-w-4xl mx-auto text-center mb-20">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Introduksjon</p>
            <h2 className="font-heading text-5xl md:text-6xl font-light leading-[1.2] mb-8 text-[#2a1810]">
              Fra måling til<br /><span className="italic">ferdig fundament</span>
            </h2>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
              En badstue er kun så solid som fundamentet den står på. Uansett hvor vakker arkitekturen er, må konstruksjonen være nøye planlagt og utført med presisjon.
            </p>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed">
              Prosessen består av seks kritiske trinn — fra første gangs områdeanalyse til sluttkontroll. Hvert trinn er avgjørende for langsiktig stabilitet og sikkerhet.
            </p>
          </div>
        </motion.section>

        {/* SECTIONS */}
        <div className="py-20">
          {sections.map((section, idx) => (
            <motion.section
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.05 }}
              className="mb-24 pb-12 border-b border-border/20 last:border-b-0"
            >
              <div className="max-w-4xl">
                <div className="mb-8">
                  <span className="font-heading text-6xl font-light text-[#2d5a3d]/30">{section.number}</span>
                </div>
                <h3 className="font-heading text-4xl font-light mb-6 text-[#2a1810]">
                  {section.title}
                </h3>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-8">
                  {section.description}
                </p>

                {/* Details list */}
                {section.details && (
                  <div className="mb-8 bg-secondary/30 rounded-[8px] p-8">
                    <p className="font-heading text-sm tracking-widest uppercase text-[#2d5a3d] mb-4">Skal fastsettes:</p>
                    <ul className="space-y-3">
                      {section.details.map((detail, i) => (
                        <li key={i} className="flex gap-3 text-foreground/70">
                          <span className="text-primary font-light text-xl">•</span>
                          <span className="font-body text-2xl">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Conclusion */}
                {section.conclusion && (
                  <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8 mb-8">
                    <p className="font-body text-2xl text-foreground/70 leading-relaxed">{section.conclusion}</p>
                  </div>
                )}

                {/* Subsections */}
                {section.subsections && (
                  <div className="space-y-6">
                    {section.subsections.map((subsection, i) => (
                      <motion.div
                        key={i}
                        whileHover={{ y: -4 }}
                        className="bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all"
                      >
                        <h4 className="font-heading text-xl font-light text-[#2a1810] mb-3">
                          {subsection.title}
                        </h4>
                        <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                          {subsection.desc}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.section>
          ))}
        </div>

        {/* SUMMARY */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="max-w-4xl">
            <h2 className="font-heading text-4xl font-light mb-8 text-[#2a1810]">
              Prosessen oppsummert
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {[
                { num: '1', label: 'Analyse av tomten' },
                { num: '2', label: 'Valg av system' },
                { num: '3', label: 'Forberedelse av terrenget' },
                { num: '4', label: 'Utførelse av fundamentet' },
                { num: '5', label: 'Montering av konstruksjon' },
                { num: '6', label: 'Installasjoner og avslutning' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4 }}
                  className="flex gap-4 bg-white border border-border/20 rounded-[8px] p-6 hover:border-[#2d5a3d] transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-[#2a1810] text-white flex items-center justify-center font-heading font-light text-lg flex-shrink-0">
                    {item.num}
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="font-heading text-2xl font-light text-[#2a1810]">{item.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12 mb-12">
              <h3 className="font-heading text-3xl font-light mb-6">Nøkkelinnsikt</h3>
              <p className="font-body text-2xl text-white/80 leading-relaxed mb-6">
                Den viktigste faktoren som påvirker hele konstruksjonens varighet er riktig forberedelse av underlaget. Selv det beste fundamentsystemet vil ikke fungere som det skal dersom ikke grunnen er forberedt på riktig måte.
              </p>
              <p className="font-body text-2xl text-white/80 leading-relaxed">
                Investering i solid fundament er investering i sikkerhet og varighet av badstuen i tiår fremover.
              </p>
            </div>

            <div className="bg-[#2d5a3d]/10 border border-[#2d5a3d]/20 rounded-[8px] p-12">
              <h3 className="font-heading text-2xl font-light text-[#2a1810] mb-4">Anbefaling for prosjekter i naturlige områder</h3>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-4">
                For prosjekter gjennomført i naturlige omgivelser anbefales søylefundament (søylefundament), som:
              </p>
              <ul className="space-y-3 ml-4">
                <li className="flex gap-3 text-foreground/70">
                  <span className="text-primary font-light text-xl">✓</span>
                  <span className="font-body text-2xl">Minimaliserer inngrep i terrenget</span>
                </li>
                <li className="flex gap-3 text-foreground/70">
                  <span className="text-primary font-light text-xl">✓</span>
                  <span className="font-body text-2xl">Muliggjør rask montering</span>
                </li>
                <li className="flex gap-3 text-foreground/70">
                  <span className="text-primary font-light text-xl">✓</span>
                  <span className="font-body text-2xl">Tillater demontering og relokering av objektet i fremtiden</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* FUNDAMENT SERVICE RECOMMENDATION */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="max-w-4xl mb-16">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Anbefalt Partner</p>
            <h2 className="font-heading text-4xl font-light mb-8 text-[#2a1810]">
              Vi anbefaler <span className="italic">Fundament Service AS</span>
            </h2>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-8">
              For profesjonell utførelse av søylefundamenter anbefaler vi Fundament Service AS som operer i hele Norge. De spesialiserer seg i miljøvennlige jordskruefundamenter som er CE-sertifisert, har 25 års garanti og kan gjenbrukes.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { title: 'Miljøvennlig', desc: 'Jordskruer laget av resirkulerbart materiale' },
                { title: 'Rask prosess', desc: 'Opptil 5 ganger kortere prosjekttid enn betongfundament' },
                { title: '25 års garanti', desc: 'CE-sertifisert og gjenbrukbar løsning' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-border/20 rounded-[8px] p-6 hover:border-[#2d5a3d] transition-all"
                >
                  <h3 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.title}</h3>
                  <p className="font-body text-2xl text-foreground/70">{item.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.a
              whileHover={{ scale: 1.05 }}
              href="https://fundamentservice.no/fundamentering.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all"
            >
              Besøk Fundament Service <ArrowRight size={16} />
            </motion.a>
          </div>

          {/* IMAGES FROM FUNDAMENT SERVICE */}
          <div className="max-w-6xl mb-16">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-8">Eksempler på arbeid</p>
            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="rounded-[8px] overflow-hidden h-80"
              >
                <img
                  src="https://fundamentservice.no/images/IMG_9594-1_1.avif"
                  alt="Jordskruer installert"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-[8px] overflow-hidden h-80"
              >
                <img
                  src="https://fundamentservice.no/images/158561a9-4d88-4c90-b0b9-8555b7329f1e_1.avif"
                  alt="Søylefundament montasje"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="rounded-[8px] overflow-hidden h-80"
              >
                <img
                  src="https://fundamentservice.no/images/vikersund4_1.avif"
                  alt="Ferdig fundament"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12 md:p-20 text-center">
            <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste steg</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.2] mb-6">
              Klar for å bygge?
            </h2>
            <p className="font-body text-2xl text-white/80 mb-12 max-w-2xl mx-auto">
              Nå som du vet hvordan man forbereder fundamentet, kan du velge din ideelle badstue og begynne konfigurasjonen.
            </p>
            <motion.a
              whileHover={{ scale: 1.05 }}
              href="/laer-mer-handverk"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all"
            >
              Tilbake til Lær Mer <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.section>

      </div>

      <Footer />
    </div>
  );
}